import { useEffect } from 'react';
import {
  BREAKPOINT_DESKTOP,
  BREAKPOINT_TABLET,
  MIN_CONTENT_WIDTH,
  SHELL_GAP,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_WIDTH,
} from '../constants/breakpoints';
import { ui$, AI_PANEL_MIN_WIDTH } from '../store/ui$';
import { getActiveRightPanel } from '../lib/rightPanelRegistry';

/**
 * Width the content rail ends up with for a given shell configuration.
 * Mirrors the .app-main margins in App.css: an expanded sidebar owns its full width,
 * a collapsed one keeps its icon rail, and the right rail is the panel plus the shell gap.
 */
export const getContentWidth = ({ viewportWidth, sidebarExpanded, panelWidth }) =>
  viewportWidth
  - (sidebarExpanded ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH)
  - SHELL_GAP
  - panelWidth;

/**
 * Width an open right panel takes away from the content. Below the desktop breakpoint the
 * panel floats at a fixed width instead of docking, but it still covers the content it sits
 * over, so it counts against the rail either way.
 */
const getRightPanelWidth = (viewportWidth) => {
  if (!getActiveRightPanel()) return 0;
  return viewportWidth > BREAKPOINT_DESKTOP
    ? (ui$.aiPanelWidth.get() || AI_PANEL_MIN_WIDTH)
    : AI_PANEL_MIN_WIDTH;
};

/**
 * Keeps the content rail at or above MIN_CONTENT_WIDTH as the viewport narrows by giving
 * chrome back to the content, in order: collapse the sidebar first, close the right panel
 * only if the viewport keeps shrinking past the floor without it.
 *
 * One-way on purpose — nothing reopens on widening, so a reclaimed panel never reappears
 * over content the reader has moved on to.
 */
export const useContentWidthGuard = () => {
  useEffect(() => {
    const enforce = () => {
      const viewportWidth = window.innerWidth;
      // Below the tablet breakpoint the sidebar is an overlay rail that costs the content
      // nothing, so there is no docked chrome left to reclaim.
      if (viewportWidth <= BREAKPOINT_TABLET) return;

      const panelWidth = getRightPanelWidth(viewportWidth);

      if (
        !ui$.sidebarCollapsed.get()
        && getContentWidth({ viewportWidth, sidebarExpanded: true, panelWidth }) < MIN_CONTENT_WIDTH
      ) {
        ui$.sidebarCollapsed.set(true);
      }

      // Sidebar space is already given back at this point; the panel is the last rail left.
      if (getContentWidth({ viewportWidth, sidebarExpanded: false, panelWidth }) < MIN_CONTENT_WIDTH) {
        getActiveRightPanel()?.close();
      }
    };

    enforce();
    window.addEventListener('resize', enforce);
    return () => window.removeEventListener('resize', enforce);
  }, []);
};

export default useContentWidthGuard;
